<template>
  <div class="predicate-input-wrap">

    <!-- Display row -->
    <div class="tray-display-row">
      <div class="tray-display-wrap" ref="inputWrapEl" :class="{ 'has-overflow': hasOverflow }">
        <input
            ref="inputEl"
            class="tray-display"
            type="text"
            v-model="rawInput"
            placeholder="Tap tokens or type a predicate…"
            aria-label="Predicate input"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
        />
      </div>
      <div class="tray-actions">
        <button
            class="sym-btn sym-btn--action"
            :class="{ 'sym-btn--locked': !isLoggedIn }"
            :title="isLoggedIn ? 'Generate a predicate' : 'Log in to generate predicates'"
            :aria-label="isLoggedIn ? 'Generate a predicate' : 'Log in to generate predicates'"
            :disabled="!enableGeneration"
            @mousedown.prevent="randomSentence()"
            @touchend.prevent="randomSentence()"
        >
          <i :class="isLoggedIn ? 'ri-loop-right-ai-line' : 'ri-lock-line'" class="sym-btn-icon" aria-hidden="true"></i>
          <span class="sym-btn-label">{{ enableGeneration ? 'Generate' : '...' }}</span>
        </button>
        <button
            class="sym-btn sym-btn--action"
            :title="showingSyntax ? 'Show visualization' : 'Show syntax tree'"
            @mousedown.prevent="toggleSyntax(!showingSyntax)"
            @touchend.prevent="toggleSyntax(!showingSyntax)"
        >
          <i :class="showingSyntax ? 'ri-hexagon-line' : 'ri-node-tree'" class="sym-btn-icon" aria-hidden="true"></i>
          <span class="sym-btn-label">{{ showingSyntax ? 'Visualize' : 'Syntax' }}</span>
        </button>
        <button class="sym-btn sym-btn--action" title="Delete last word"
            @mousedown.prevent="backspace"
            @touchend.prevent="backspace"
        ><i class="ri-delete-back-2-line sym-btn-icon" aria-hidden="true"></i><span class="sym-btn-label sym-btn-label--backspace">Delete</span></button>
        <button
            class="sym-btn sym-btn--action sym-btn--home"
            title="Go home"
            @mousedown.prevent="emit('home')"
            @touchend.prevent="emit('home')"
        ><i class="ri-home-5-line sym-btn-icon" aria-hidden="true"></i><span class="sym-btn-label">Home</span></button>
        <button class="sym-btn sym-btn--action sym-btn--clear" title="Clear all"
            @mousedown.prevent="clear"
            @touchend.prevent="clear"
        ><i class="ri-close-line sym-btn-icon" aria-hidden="true"></i><span class="sym-btn-label">Clear</span></button>
        <Transition name="hint">
          <p v-if="showLoginHint" class="login-hint" :class="{ 'login-hint--up': props.drawerOpensUp }">
            <router-link to="/account">Log in</router-link> to generate predicates
          </p>
        </Transition>
        <!-- Mobile expand toggle (portrait only) -->
        <button
            class="sym-btn sym-btn--expand"
            :title="drawerOpen ? 'Collapse token tray' : 'Expand token tray'"
            :aria-expanded="drawerOpen.toString()"
            @mousedown.prevent="toggleDrawer"
            @touchend.prevent="toggleDrawer"
        >
          <i :class="drawerOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" class="sym-btn-icon" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <!-- Token tray: always visible on desktop/landscape, drawer on mobile portrait -->
    <Transition name="drawer-slide" @enter="onTrayEnter" @after-enter="onTrayAfterEnter" @leave="onTrayLeave">
      <div v-show="showFullTray" class="tray-full" :class="{ 'tray-full--down': !props.drawerOpensUp }" role="group" aria-label="All predicate tokens">

        <div class="tray-row tray-row--logic">
          <span class="tray-row__label">Logic</span>
          <div class="tray-group">
            <button v-for="tok in quantifiers" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
            <button v-for="tok in connectives" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
            <button v-for="tok in structureTokens" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

        <div class="tray-row tray-row--quantifiers">
          <span class="tray-row__label">Quantifiers</span>
          <div class="tray-group">
            <button v-for="tok in quantifiers" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

        <div class="tray-row tray-row--connectives">
          <span class="tray-row__label">Connectives</span>
          <div class="tray-group">
            <button v-for="tok in connectives" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

        <div class="tray-row tray-row--structure">
          <span class="tray-row__label">Structure</span>
          <div class="tray-group">
            <button v-for="tok in structureTokens" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

        <div class="tray-row">
          <span class="tray-row__label">Color/Shape</span>
          <div class="tray-group">
            <button v-for="tok in colorTokens" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
            <span class="tray-group-divider" aria-hidden="true"></span>
            <button v-for="tok in shapeTokens" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

        <div class="tray-row">
          <span class="tray-row__label">Size</span>
          <div class="tray-group">
            <button v-for="tok in sizeTokens" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

        <div class="tray-row">
          <span class="tray-row__label">Position</span>
          <div class="tray-group">
            <button v-for="tok in spatialTokens" :key="tok.label"
                class="sym-btn sym-btn--pred" :title="tok.title"
                @mousedown.prevent="tapToken(tok.value)"
                @touchend.prevent="tapToken(tok.value)"
            >{{ tok.label }}</button>
          </div>
        </div>

      </div>
    </Transition>


  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { getRandomSentence } from "@/composables/challenges";
import { useAuth } from "@/composables/useAuth";

const props = defineProps({
  drawerOpensUp: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["predicateInputChanged", "showSyntaxChanged", "home"]);

const { isLoggedIn } = useAuth();

const showingSyntax = ref(false);
const enableGeneration = ref(true);
const showLoginHint = ref(false);
let loginHintTimeout = null;
const rawInput = ref("");
const inputEl = ref(null);
const inputWrapEl = ref(null);
const hasOverflow = ref(false);
const drawerOpen = ref(true);
const isMobilePortrait = ref(false);

// The expand/collapse toggle controls the tray on every layout.
const showFullTray = computed(() => drawerOpen.value);

function checkLayout() {
  const portrait = window.matchMedia("(orientation: portrait)").matches;
  isMobilePortrait.value = portrait && window.innerWidth < 640;
}

onMounted(() => {
  checkLayout();
  // Mobile portrait starts collapsed (tray is an overlay there); other layouts start open.
  drawerOpen.value = !isMobilePortrait.value;
  window.addEventListener('resize', checkLayout);
  window.addEventListener('orientationchange', checkLayout);
});
onUnmounted(() => {
  window.removeEventListener('resize', checkLayout);
  window.removeEventListener('orientationchange', checkLayout);
  clearTimeout(loginHintTimeout);
});

function checkOverflow() {
  if (inputEl.value) {
    hasOverflow.value = inputEl.value.scrollWidth > inputEl.value.clientWidth;
  }
}

function tapToken(value) {
  const current = rawInput.value;
  const hasSelection = inputEl.value && document.activeElement === inputEl.value;
  const cursorPos = hasSelection && inputEl.value.selectionStart != null
    ? inputEl.value.selectionStart
    : current.length;

  const before = current.slice(0, cursorPos);
  const after = current.slice(cursorPos);
  const needsLeadingSpace = before.length > 0 && !before.endsWith(' ') && !after.startsWith(' ');
  const insertion = needsLeadingSpace ? ' ' + value : value;
  const needsTrailingSpace = after.length > 0 && !after.startsWith(' ');

  rawInput.value = before + insertion + (needsTrailingSpace ? ' ' : '') + after;
  const newCursorPos = before.length + insertion.length + (needsTrailingSpace ? 1 : 0);

  nextTick(() => {
    if (inputEl.value) {
      inputEl.value.focus();
      inputEl.value.setSelectionRange(newCursorPos, newCursorPos);
    }
    checkOverflow();
  });
}

function backspace() {
  const trimmed = rawInput.value.trimEnd();
  const lastSpace = trimmed.lastIndexOf(' ');
  rawInput.value = lastSpace >= 0 ? trimmed.slice(0, lastSpace) : '';
  nextTick(checkOverflow);
}

function clear() {
  rawInput.value = '';
  nextTick(checkOverflow);
}

function toggleDrawer() {
  drawerOpen.value = !drawerOpen.value;
}

// Height-collapse transition hooks — used on non-mobile layouts where the tray
// sits inline (not absolutely positioned), so the page height changes visibly.
function onTrayEnter(el) {
  el.style.overflow = 'hidden';
  el.style.height = '0';
  el.style.opacity = '0';
  requestAnimationFrame(() => {
    el.style.height = el.scrollHeight + 'px';
    el.style.opacity = '1';
  });
}
function onTrayAfterEnter(el) {
  el.style.overflow = '';
  el.style.height = '';
  el.style.opacity = '';
}
function onTrayLeave(el) {
  el.style.overflow = 'hidden';
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
  requestAnimationFrame(() => {
    el.style.height = '0';
    el.style.opacity = '0';
  });
}

function toggleSyntax(val) {
  showingSyntax.value = val;
  emit("showSyntaxChanged", val);
}

async function randomSentence() {
  if (!isLoggedIn.value) {
    clearTimeout(loginHintTimeout);
    showLoginHint.value = true;
    loginHintTimeout = setTimeout(() => { showLoginHint.value = false; }, 4000);
    return;
  }
  enableGeneration.value = false;
  const sentence = await getRandomSentence();
  if (typeof sentence === "string" && sentence.length > 3) {
    rawInput.value = sentence;
  }
  enableGeneration.value = true;
}

watch(rawInput, val => {
  emit("predicateInputChanged", val);
  nextTick(checkOverflow);
});

// Token definitions
const quantifiers = [
  { label: "∀x", value: "∀x", title: "Universal quantifier" },
  { label: "∃x", value: "∃x", title: "Existential quantifier" },
];

const connectives = [
  { label: "¬",  value: "¬",  title: "Negation" },
  { label: "∧",  value: "∧",  title: "Conjunction (AND)" },
  { label: "∨",  value: "∨",  title: "Disjunction (OR)" },
  { label: "→︎", value: "→",  title: "Implication" },
  { label: "↔︎", value: "↔",  title: "Biconditional" },
  { label: "⊕",  value: "⊕",  title: "XOR" },
  { label: "⊼",  value: "⊼",  title: "NAND" },
  { label: "⊽",  value: "⊽",  title: "NOR" },
];

const colorTokens = [
  { label: "Red(x)",    value: "Red(x)",    title: "Red" },
  { label: "Yellow(x)", value: "Yellow(x)", title: "Yellow" },
  { label: "Grey(x)",   value: "Grey(x)",   title: "Grey" },
  { label: "Green(x)",  value: "Green(x)",  title: "Green" },
  { label: "Blue(x)",   value: "Blue(x)",   title: "Blue" },
];

const shapeTokens = [
  { label: "Triangle(x)", value: "Triangle(x)", title: "Triangle" },
  { label: "Square(x)",   value: "Square(x)",   title: "Square" },
  { label: "Hexagon(x)",  value: "Hexagon(x)",  title: "Hexagon" },
];

const sizeTokens = [
  { label: "Small(x)",      value: "Small(x)",      title: "Small" },
  { label: "Medium(x)",     value: "Medium(x)",     title: "Medium" },
  { label: "Large(x)",      value: "Large(x)",      title: "Large" },
  { label: "Smaller(x,y)",  value: "Smaller(x, y)", title: "Smaller than" },
  { label: "Larger(x,y)",   value: "Larger(x, y)",  title: "Larger than" },
  { label: "SameSize(x,y)", value: "SameSize(x, y)", title: "Same size" },
];

const spatialTokens = [
  { label: "LeftOf(x,y)",  value: "LeftOf(x, y)",  title: "Left of" },
  { label: "Above(x,y)",   value: "Above(x, y)",   title: "Above" },
  { label: "Below(x,y)",   value: "Below(x, y)",   title: "Below" },
  { label: "SameCol(x,y)", value: "SameCol(x, y)", title: "Same column" },
  { label: "SameRow(x,y)", value: "SameRow(x, y)", title: "Same row" },
  { label: "Between(x,y,z)", value: "Between(x, y, z)", title: "Between" },
];

const structureTokens = [
  { label: "(",  value: "(",  title: "Open parenthesis" },
  { label: ")",  value: ")",  title: "Close parenthesis" },
  { label: "∈",  value: "∈",  title: "Member of" },
  { label: "∉",  value: "∉",  title: "Not member of" },
];
</script>

<style scoped>
.predicate-input-wrap {
  width: 100%;
  position: relative;
}

/* ── Display row ── */
.tray-display-row {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 4px;
  margin-bottom: 6px;
}

.tray-display-wrap {
  flex: 1 1 0;
  min-width: 0;
  position: relative;
}

.tray-display-wrap::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 32px;
  pointer-events: none;
  background: linear-gradient(to right, transparent, var(--background));
  opacity: 0;
  transition: opacity 0.15s ease;
}

.tray-display-wrap.has-overflow::after {
  opacity: 1;
}

.tray-display {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid var(--border-color-bright);
  background: var(--background);
  color: var(--text-color-bright);
  font-family: var(--font), monospace;
  font-size: 15px;
  white-space: nowrap;
  box-sizing: border-box;
  /* Reset input defaults */
  outline: none;
  appearance: none;
  border-radius: 0;
}

.tray-display::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.tray-display:focus {
  border-color: var(--text-color-bright);
}

.tray-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

/* ── Shared button base — Wordle tile style ── */
.sym-btn {
  height: 44px;
  min-width: 44px;
  padding: 0 10px;
  background-color: var(--highlighted);   /* #3a3a3a — Wordle-grey tile */
  border: none;
  border-radius: 4px;
  color: var(--text-color-bright);
  font-family: var(--font), monospace;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: background-color 0.1s, transform 0.08s;
  user-select: none;
  line-height: 1;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  /* Keycap depth — inset shadow gives tile a slight lift without skeuomorphism */
  box-shadow: inset 0 -2px 0 rgba(0,0,0,0.35);
}

.sym-btn:hover {
  background-color: #4a4a4a;
}

.sym-btn:active {
  transform: translateY(1px);
  box-shadow: none;
}

.sym-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

/* Locked (guest) state — dimmed but still tappable: the tap reveals the login hint */
.sym-btn--locked {
  opacity: 0.5;
}

.sym-btn--locked:hover {
  background-color: transparent;
  border-color: var(--border-color-bright);
}

/* ── Login hint (guest taps a locked action) ── */
.login-hint {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 11; /* above the mobile tray drawer (10) */
  margin: 0;
  padding: 6px 10px;
  width: max-content;
  max-width: min(240px, 100%);
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-color-bright);
  background: var(--background);
  border: 1px solid var(--border-color-bright);
  text-align: right;
}

.login-hint a {
  color: var(--tomorrows-harvest);
}

/* Tray sits at the bottom of the viewport (board above) — open the hint upward */
.login-hint--up {
  top: auto;
  bottom: calc(100% + 8px);
}

.hint-enter-active {
  transition: opacity 0.2s var(--ease-out-quart), transform 0.2s var(--ease-out-quart);
}

.hint-leave-active {
  transition: opacity 0.15s ease-in;
}

.hint-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.login-hint--up.hint-enter-from {
  transform: translateY(4px);
}

.hint-leave-to {
  opacity: 0;
}

/* Predicate tokens (longer labels) — slightly smaller text, same tile feel */
.sym-btn--pred {
  font-size: 11px;
  letter-spacing: 0;
  padding: 0 8px;
  height: 40px;
  min-width: unset;
}

/* Action buttons (Generate, Syntax, ⌫) — slightly distinct: bordered rather than filled */
.sym-btn--action {
  height: auto;
  align-self: stretch;
  padding: 0 12px;
  background-color: transparent;
  border: 1px solid var(--border-color-bright);
  box-shadow: none;
  border-radius: 0;
  font-weight: 600;
}

.sym-btn--action:hover {
  background-color: var(--hover-bg);
  border-color: var(--hover-border);
}

.sym-btn--action:active {
  transform: none;
  background-image: var(--hatch);
}

.sym-btn--expand {
  display: flex;
}

/* Home button — landscape mobile tray only */
.sym-btn--home {
  display: none;
}

/* Clear button hidden on mobile portrait (⌫ is enough) */
.sym-btn--clear {
  display: none;
}


/* ── Combined logic row: landscape-only (hidden by default) ── */
.tray-row--logic {
  display: none;
}

/* ── Token groups ── */
.tray-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Fast row groups wrap naturally — connectives flow to next line on narrow screens */


/* ── Full tray (rows of groups) ── */
.tray-full {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 2px;
}

/* Thin rule between groups — replaces uppercase eyebrows */
.tray-row + .tray-row {
  border-top: 1px solid var(--border-color);
  padding-top: 6px;
}

.tray-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.tray-row__label {
  width: 60px;
  flex-shrink: 0;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: none;
  color: var(--text-color);
  line-height: 40px;
  opacity: 0.6;
}

/* Icon / label visibility */
.sym-btn-icon {
  font-size: 18px;
  line-height: 1;
}

/* ── Drawer transition ── */
.drawer-slide-enter-active {
  transition: height 0.26s var(--ease-out-expo), opacity 0.22s var(--ease-out-quart);
}
.drawer-slide-leave-active {
  transition: height 0.2s var(--ease-out-quart), opacity 0.16s ease;
}
.drawer-slide-enter-from {
  opacity: 0;
}
.drawer-slide-leave-to {
  opacity: 0;
}

.tray-full--down {
  --drawer-enter-y: -8px;
  --drawer-leave-y: -4px;
}

/* ── Backdrop ── */
.tray-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9;
  background: transparent;
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.15s ease;
}
.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

/* ── Landscape mobile (side-by-side layout in ExploreView) ── */
@media screen and (max-height: 639px) and (orientation: landscape) {
  .sym-btn {
    height: 32px;
    min-width: 32px;
    font-size: 12px;
  }

  .sym-btn--pred {
    font-size: 10px;
    height: 32px;
  }

  .tray-display {
    height: 36px;
    font-size: 13px;
  }

  .tray-row__label {
    display: none;
  }

  .sym-btn-icon {
    font-size: 15px;
  }

  /* Icon-only labels */
  .sym-btn-label {
    display: none;
  }

  /* Input takes full width; actions wrap to a row below */
  .tray-display-row {
    flex-wrap: wrap;
    margin-bottom: 3px;
  }

  .tray-display-wrap {
    flex: 1 1 100%;
    min-width: 0;
  }

  .tray-display {
    width: 100%;
  }

  .tray-actions {
    gap: 3px;
  }

  .sym-btn--action {
    height: 32px;
    align-self: auto;
    min-width: 32px;
    padding: 0 8px;
  }

  /* Show combined logic row, hide individual rows */
  .tray-row--logic {
    display: flex;
  }

  .tray-row--quantifiers,
  .tray-row--connectives,
  .tray-row--structure {
    display: none;
  }

  .tray-full {
    gap: 2px;
    padding-top: 0;
  }

  .tray-row + .tray-row {
    padding-top: 3px;
  }

  .tray-group {
    gap: 3px;
  }

  .sym-btn--clear {
    display: flex;
  }

  .sym-btn--home {
    display: flex;
  }
}

/* ── Desktop: show Clear label ── */
@media screen and (min-width: 640px) {
  .sym-btn--clear {
    display: flex;
  }
}

/* ── Mobile portrait ── */
@media screen and (max-width: 639px) and (orientation: portrait) {
  /* Stack display row: input takes full width on top, actions row below */
  .tray-display-row {
    flex-wrap: wrap;
    gap: 4px;
  }

  .tray-display {
    flex: 1 1 100%;
  }

  /* Actions row spans full width — buttons stretch to fill it */
  .tray-actions {
    width: 100%;
    gap: 4px;
  }

  /* Generate and Syntax grow to fill available space */
  .tray-actions .sym-btn--action:not(.sym-btn--expand):not(.sym-btn--clear) {
    flex: 1;
  }

  /* ⌫ and expand stay fixed-width, label hidden */
  .tray-actions .sym-btn--expand,
  .tray-actions .sym-btn--action:has(.ri-delete-back-2-line) {
    flex: 0 0 44px;
  }

  .sym-btn-label--backspace {
    display: none;
  }

  /* Clear not needed on mobile portrait — ⌫ suffices */
  .sym-btn--clear {
    display: none;
  }

  /* Generate is the leftmost action here — anchor the hint under it */
  .login-hint {
    right: auto;
    left: 0;
    text-align: left;
  }

  /* Drawer: floats above the tray (wrapper is at page bottom) */
  .tray-full {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    z-index: 10;
    background: var(--background);
    border: 1px solid var(--border-color-bright);
    border-bottom: none;
    padding: 12px 10px 8px;
    gap: 6px;
  }

  /* Drawer opens downward (tray is near the top, no world yet) */
  .tray-full--down {
    bottom: auto;
    top: 100%;
    border-bottom: 1px solid var(--border-color-bright);
    border-top: none;
  }

  .tray-row {
    flex-wrap: wrap;
    gap: 6px;
  }

  .tray-row__label {
    display: none;
  }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .drawer-slide-enter-active,
  .drawer-slide-leave-active,
  .backdrop-fade-enter-active,
  .backdrop-fade-leave-active,
  .hint-enter-active,
  .hint-leave-active {
    transition: none;
  }

  .hint-enter-from {
    transform: none;
  }
}
</style>
